import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment,
  Fab,
  LinearProgress
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface Office {
  _id: string;
  name: string;
  description: string;
  address: string;
  manager?: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  isActive: boolean;
  settings: {
    maxEmployees?: number;
    allowSelfRegistration: boolean;
  };
  stats: {
    totalEmployees: number;
    activeEmployees: number;
    totalProgress: number;
    utilizationRate?: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface OfficeFormData {
  name: string;
  description: string;
  address: string;
  manager: string;
  maxEmployees: string;
  allowSelfRegistration: boolean;
}

const CompanyOffices: React.FC = () => {
  const { user } = useAuth();
  const [offices, setOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Estados para el diálogo de formulario
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingOffice, setEditingOffice] = useState<Office | null>(null);
  const [formData, setFormData] = useState<OfficeFormData>({
    name: '',
    description: '',
    address: '',
    manager: '',
    maxEmployees: '',
    allowSelfRegistration: true
  });
  const [formLoading, setFormLoading] = useState(false);
  
  // Estados para el diálogo de detalles
  const [selectedOffice, setSelectedOffice] = useState<Office | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [officeEmployees, setOfficeEmployees] = useState<any[]>([]);
  
  // Estados para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // Lista de empleados para seleccionar como manager
  const [employees, setEmployees] = useState<any[]>([]);

  useEffect(() => {
    loadOffices();
    loadEmployees();
  }, []);

  const loadOffices = async () => {
    try {
      setLoading(true);
      const response = await api.getOffices();
      setOffices(response);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error cargando sedes',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await api.getCompanyEmployees();
      setEmployees(response.employees || []);
    } catch (err: any) {
      console.error('Error cargando empleados:', err);
    }
  };

  const handleOpenFormDialog = (office?: Office) => {
    if (office) {
      setEditingOffice(office);
      setFormData({
        name: office.name,
        description: office.description || '',
        address: office.address || '',
        manager: office.manager?._id || '',
        maxEmployees: office.settings.maxEmployees?.toString() || '',
        allowSelfRegistration: office.settings.allowSelfRegistration
      });
    } else {
      setEditingOffice(null);
      setFormData({
        name: '',
        description: '',
        address: '',
        manager: '',
        maxEmployees: '',
        allowSelfRegistration: true
      });
    }
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setEditingOffice(null);
    setFormData({
      name: '',
      description: '',
      address: '',
      manager: '',
      maxEmployees: '',
      allowSelfRegistration: true
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const officeData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        address: formData.address.trim(),
        manager: formData.manager || null,
        settings: {
          maxEmployees: formData.maxEmployees ? parseInt(formData.maxEmployees) : null,
          allowSelfRegistration: formData.allowSelfRegistration
        }
      };

      if (editingOffice) {
        await api.updateOffice(editingOffice._id, officeData);
        setSnackbar({
          open: true,
          message: 'Sede actualizada exitosamente',
          severity: 'success'
        });
      } else {
        await api.createOffice(officeData);
        setSnackbar({
          open: true,
          message: 'Sede creada exitosamente',
          severity: 'success'
        });
      }

      handleCloseFormDialog();
      loadOffices();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error guardando sede',
        severity: 'error'
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteOffice = async (office: Office) => {
    if (!window.confirm(`¿Está seguro de que desea eliminar la sede "${office.name}"?`)) {
      return;
    }

    try {
      await api.deleteOffice(office._id);
      setSnackbar({
        open: true,
        message: 'Sede eliminada exitosamente',
        severity: 'success'
      });
      loadOffices();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error eliminando sede',
        severity: 'error'
      });
    }
  };

  const handleViewOfficeDetails = async (office: Office) => {
    try {
      setDetailLoading(true);
      setSelectedOffice(office);
      
      const response = await api.getOfficeEmployees(office._id);
      const employees = response.data?.employees || response.employees || [];
      setOfficeEmployees(employees);
      setDetailDialogOpen(true);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error cargando detalles de la sede',
        severity: 'error'
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const getStatusLabel = (isActive: boolean) => isActive ? 'Activa' : 'Inactiva';
  const getStatusColor = (isActive: boolean) => isActive ? 'success' : 'error';

  const filteredOffices = offices.filter(office => {
    const matchesSearch = office.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         office.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         office.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && office.isActive) ||
                         (statusFilter === 'inactive' && !office.isActive);
    
    return matchesSearch && matchesStatus;
  });

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          <LocationIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Gestión de Sedes
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Administra las sedes de tu empresa
        </Typography>
      </Box>

      {/* Filtros y búsqueda */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
            alignItems: 'end'
          }}>
            <TextField
              label="Buscar sedes"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
            
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Estado"
              >
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="active">Activas</MenuItem>
                <MenuItem value="inactive">Inactivas</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography variant="body2" color="text.secondary">
                Total: {filteredOffices.length} sedes
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tarjetas de sedes */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        gap: 3 
      }}>
        {loading ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <LinearProgress />
          </Box>
        ) : filteredOffices.length === 0 ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="text.secondary">
                  No se encontraron sedes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Intenta ajustar los filtros de búsqueda'
                    : 'Crea tu primera sede para comenzar'
                  }
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ) : (
          filteredOffices.map((office) => (
            <Box key={office._id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="h3">
                      {office.name}
                    </Typography>
                    <Chip
                      label={getStatusLabel(office.isActive)}
                      color={getStatusColor(office.isActive)}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {office.description || 'Sin descripción'}
                  </Typography>

                  {office.address && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      📍 {office.address}
                    </Typography>
                  )}

                  {office.manager && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Gerente:
                      </Typography>
                      <Typography variant="body2">
                        {office.manager.firstName} {office.manager.lastName}
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PeopleIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {office.stats.totalEmployees} empleados
                      </Typography>
                    </Box>
                    
                    {office.settings.maxEmployees && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrendingUpIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {Math.round(office.stats.utilizationRate || 0)}%
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {office.settings.maxEmployees && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Capacidad
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {office.stats.totalEmployees} / {office.settings.maxEmployees}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={office.stats.utilizationRate || 0}
                        color={office.stats.utilizationRate && office.stats.utilizationRate > 90 ? 'error' : 'primary'}
                      />
                    </Box>
                  )}
                </CardContent>
                
                <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Tooltip title="Ver detalles">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleViewOfficeDetails(office)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <IconButton 
                      size="small" 
                      color="primary"
                      onClick={() => handleOpenFormDialog(office)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Eliminar">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteOffice(office)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            </Box>
          ))
        )}
      </Box>

      {/* Botón flotante para agregar */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => handleOpenFormDialog()}
      >
        <AddIcon />
      </Fab>

      {/* Diálogo de formulario */}
      <Dialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingOffice ? 'Editar Sede' : 'Nueva Sede'}
        </DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Nombre de la sede"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
              />
              
              <TextField
                label="Descripción"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />

              <TextField
                label="Dirección"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Gerente de la sede</InputLabel>
                <Select
                  value={formData.manager}
                  onChange={(e) => setFormData({ ...formData, manager: e.target.value })}
                  label="Gerente de la sede"
                >
                  <MenuItem value="">Sin gerente asignado</MenuItem>
                  {employees.map((employee) => (
                    <MenuItem key={employee._id} value={employee._id}>
                      {employee.firstName} {employee.lastName} ({employee.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Máximo de empleados (opcional)"
                type="number"
                value={formData.maxEmployees}
                onChange={(e) => setFormData({ ...formData, maxEmployees: e.target.value })}
                inputProps={{ min: 1 }}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Permitir auto-registro</InputLabel>
                <Select
                  value={formData.allowSelfRegistration ? 'true' : 'false'}
                  onChange={(e) => setFormData({ ...formData, allowSelfRegistration: e.target.value === 'true' })}
                  label="Permitir auto-registro"
                >
                  <MenuItem value="true">Sí</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseFormDialog} disabled={formLoading}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={formLoading}>
              {formLoading ? 'Guardando...' : (editingOffice ? 'Actualizar' : 'Crear')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Diálogo de detalles de la sede */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setSelectedOffice(null);
          setOfficeEmployees([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <LocationIcon />
            <Typography variant="h6">
              {selectedOffice?.name}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography>Cargando detalles...</Typography>
            </Box>
          ) : selectedOffice ? (
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {selectedOffice.description || 'Sin descripción'}
              </Typography>

              {selectedOffice.address && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  📍 {selectedOffice.address}
                </Typography>
              )}

              <Typography variant="h6" gutterBottom>
                Empleados de la Sede
              </Typography>
              
              {officeEmployees.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No hay empleados asignados a esta sede
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Empleado</TableCell>
                        <TableCell>Cargo</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell>Último Acceso</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {officeEmployees.map((employee) => (
                        <TableRow key={employee._id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                                {employee.firstName.charAt(0).toUpperCase()}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">
                                  {employee.firstName} {employee.lastName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {employee.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {employee.position || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(employee.isActive)}
                              color={getStatusColor(employee.isActive)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {employee.lastLogin 
                                ? new Date(employee.lastLogin).toLocaleDateString('es-ES')
                                : 'Nunca'
                              }
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDetailDialogOpen(false);
            setSelectedOffice(null);
            setOfficeEmployees([]);
          }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CompanyOffices;

